export type BBox = [number, number, number, number];

export type Mode =
  | "obce"
  | "okresy"
  | "kraje"
  | "chkonp"
  | "praha"
  | "brno"
  | "metro"
  | "reky"
  | "hory"
  | "hory-prominence";

/** Protected-area category in the Chráněná území mode. */
export type ProtectedAreaKind = "CHKO" | "NP" | "NPP" | "NPR" | "PP" | "PR";

/** Geometry kind, used by MapView to pick the right render path. */
export type GeometryKind = "polygon" | "point" | "line";

/** Metro line letters that drive the per-station colour. */
export type MetroLine = "A" | "B" | "C";

export interface Entity {
  /** Stable RÚIAN / AOPK / DPP code, used as MapLibre feature id. */
  id: string;
  name: string;
  /**
   * Extra strings the matcher should accept in addition to `name`,
   * e.g. "Krkonošský národní park" → "Krkonoše".
   */
  aliases?: string[];
  /** Parent okres – empty string outside obce mode. */
  okres: string;
  /** Parent kraj (region) – empty string for parks (cross-region). */
  kraj: string;
  /** Current registry population (`pocoby`); 0 for parks / metro. */
  pop: number;
  /** 2021 census population (`pobyosl21`); 0 for parks / metro. */
  pop21: number;
  /** Area in m² (`vymera` / `SHAPEAREA`); 0 for metro / rivers. */
  area: number;
  /**
   * Metro-only: concatenated, sorted line letters, e.g. "B" for a
   * single-line station or "AB" / "AC" / "BC" for transfers.
   */
  lines?: string;
  /**
   * Rivers-only: haversine length in metres, summed over all
   * line parts.
   */
  length?: number;
  /** Chráněná území mode: which protected-area category this entity is. */
  kind?: ProtectedAreaKind;
  /** Peaks mode: summit elevation in metres above sea level. */
  elevation?: number;
  /** Peaks (prominence) mode: topographic prominence in metres. */
  prominence?: number;
  /** Peaks mode: parent mountain range (pohoří). */
  range?: string;
  bbox: BBox;
}

export interface DatasetIndex {
  generatedAt: string;
  count: number;
  totalPopulation: number;
  totalPopulation2021: number;
  totalAreaM2: number;
  /** Rivers-only: sum of all river lengths in metres. */
  totalLengthM?: number;
  /** Peaks-only: sum of all summit elevations in metres. */
  totalElevationM?: number;
  /** Peaks (prominence) mode: sum of all prominences in metres. */
  totalProminenceM?: number;
  /**
   * Union of all entity bboxes – the frontend uses it as the
   * initial camera frame so each mode opens already centred on its
   * data (Praha mode shouldn't zoom out to the whole country).
   * Optional for backwards compat with older index files.
   */
  bounds?: BBox;
  entities: Entity[];
}

/** Mode-specific configuration consumed by the App / MapView / hooks. */
export interface DatasetDescriptor {
  mode: Mode;
  /** URL of the simplified geojson layer for the map. */
  geojsonUrl: string;
  /** URL of the lookup index loaded into memory. */
  indexUrl: string;
  /** localStorage key for the per-mode guess set. */
  storageKey: string;

  // ── User-facing labels (Czech) ────────────────────────────────────
  /** Title of the mode in the segmented switch. */
  modeLabel: string;
  /** Genitive plural used in "X obcí / X okresů". */
  countNoun: string;
  /** Singular nominative – placeholder of the input field. */
  inputPlaceholder: string;
  /** Header of the list of guesses. */
  listTitle: string;
  /** Initial empty-list hint. */
  emptyHint: string;
  /** Label of the "count guessed" stat row. */
  statCountLabel: string;
  /** Confirm dialog before resetting. */
  resetConfirm: string;
  /** Confirm dialog before ending the current run. */
  finishConfirm: string;
  /** Input placeholder shown when the run is finished. */
  finishedPlaceholder: string;
  /**
   * Which secondary stat to render in the sidebar after the count.
   * Obce / okresy show population; parks/Praha use area; rivers use
   * total length; metro has neither, so use "none" and only the
   * count bar is shown.
   */
  secondaryStat:
    | "population2021"
    | "area"
    | "length"
    | "elevation"
    | "prominence"
    | "none";
  /**
   * Geometry kind of the underlying dataset. Polygon modes go
   * through the standard fill/outline layers; "point" mode (currently
   * just metro) uses a circle layer with line-coloured markers.
   * Defaults to "polygon" for descriptors that omit it.
   */
  geometryKind?: GeometryKind;
  /**
   * Styling variant for `geometryKind: "point"`. Metro stations use
   * line-coloured circles; peaks use a neutral grey → green scheme.
   */
  pointVariant?: "metro" | "peak";
  /**
   * When set, guessed polygon fills (and outlines) are tinted per
   * feature value of `categoryProperty` using `categoryColors`.
   */
  categoryProperty?: string;
  categoryColors?: Record<string, { fill: string; outline: string }>;
  /**
   * When true, render a simplified Czechia outline under game entities.
   * Used for modes where sparse features look odd without country context.
   */
  countryBackground?: boolean;
}

/** URL of the simplified Czechia outline used as a map background. */
export const COUNTRY_GEOJSON_URL = "/data/cr.geojson";

/** Map colours for guessed protected-area polygons by category. */
export const PROTECTED_AREA_COLORS: Record<
  ProtectedAreaKind,
  { fill: string; outline: string }
> = {
  CHKO: { fill: "#22c55e", outline: "#166534" },
  NP: { fill: "#0891b2", outline: "#0e7490" },
  NPR: { fill: "#7c3aed", outline: "#5b21b6" },
  NPP: { fill: "#2563eb", outline: "#1d4ed8" },
  PR: { fill: "#ca8a04", outline: "#a16207" },
  PP: { fill: "#84cc16", outline: "#4d7c0f" },
};

/** Display order for per-category progress in the Chráněná území mode. */
export const PROTECTED_AREA_KINDS: ProtectedAreaKind[] = [
  "NP",
  "CHKO",
  "NPR",
  "NPP",
  "PR",
  "PP",
];

/** Sidebar stat labels keyed by protected-area category. */
export const PROTECTED_AREA_STAT_LABELS: Record<ProtectedAreaKind, string> = {
  NP: "Národní parky",
  CHKO: "CHKO",
  NPR: "Národní přír. památky",
  NPP: "Národní přír. rezervace",
  PR: "Přírodní rezervace",
  PP: "Přírodní památky",
};

/**
 * Palette for the metro mode. Mirrors the real Prague metro line
 * colours, also referenced from the circle paint expression in
 * MapView.
 */
export const METRO_LINE_COLORS: Record<MetroLine, string> = {
  A: "#16a34a", // green
  B: "#eab308", // yellow / amber
  C: "#dc2626", // red
};

export const DATASETS: Record<Mode, DatasetDescriptor> = {
  obce: {
    mode: "obce",
    geojsonUrl: "/data/municipalities.geojson",
    indexUrl: "/data/municipalities-index.json",
    storageKey: "hadej-cesko:guesses:obce:v1",
    modeLabel: "Obce",
    countNoun: "obcí",
    inputPlaceholder: "Napiš název obce…",
    listTitle: "Uhádnuté obce",
    emptyHint: "Tipni první obec a uvidíš ji tady.",
    statCountLabel: "Obce",
    resetConfirm: "Opravdu chcete smazat všechny tipy obcí a začít znovu?",
    finishConfirm: "Opravdu chcete ukončit hru? Najetím na obec na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "population2021",
  },
  okresy: {
    mode: "okresy",
    geojsonUrl: "/data/districts.geojson",
    indexUrl: "/data/districts-index.json",
    storageKey: "hadej-cesko:guesses:okresy:v1",
    modeLabel: "Okresy",
    countNoun: "okresů",
    inputPlaceholder: "Napiš název okresu…",
    listTitle: "Uhádnuté okresy",
    emptyHint: "Tipni první okres a uvidíš ho tady.",
    statCountLabel: "Okresy",
    resetConfirm: "Opravdu chcete smazat všechny tipy okresů a začít znovu?",
    finishConfirm: "Opravdu chcete ukončit hru? Najetím na okres na mapě se vám zobrazí jeho název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "population2021",
  },
  kraje: {
    mode: "kraje",
    geojsonUrl: "/data/regions.geojson",
    indexUrl: "/data/regions-index.json",
    storageKey: "hadej-cesko:guesses:kraje:v1",
    modeLabel: "Kraje",
    countNoun: "krajů",
    inputPlaceholder: "Napiš název kraje…",
    listTitle: "Uhádnuté kraje",
    emptyHint: "Tipni první kraj a uvidíš ho tady.",
    statCountLabel: "Kraje",
    resetConfirm: "Opravdu chcete smazat všechny tipy krajů a začít znovu?",
    finishConfirm:
      "Opravdu chcete ukončit hru? Najetím na kraj na mapě se vám zobrazí jeho název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "population2021",
  },
  chkonp: {
    mode: "chkonp",
    geojsonUrl: "/data/chko-np.geojson",
    indexUrl: "/data/chko-np-index.json",
    storageKey: "hadej-cesko:guesses:chkonp:v1",
    modeLabel: "Chráněná území",
    countNoun: "chráněných území",
    inputPlaceholder: "Napiš název chráněného území…",
    listTitle: "Uhádnutá chráněná území",
    emptyHint: "Tipni první chráněné území a uvidíš ho tady.",
    statCountLabel: "Území",
    resetConfirm:
      "Opravdu chcete smazat všechny tipy chráněných území a začít znovu?",
    finishConfirm:
      "Opravdu chcete ukončit hru? Najetím na území na mapě se vám zobrazí jeho název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "area",
    categoryProperty: "kind",
    categoryColors: PROTECTED_AREA_COLORS,
    countryBackground: true,
  },
  praha: {
    mode: "praha",
    geojsonUrl: "/data/prague-quarters.geojson",
    indexUrl: "/data/prague-quarters-index.json",
    storageKey: "hadej-cesko:guesses:praha:v1",
    modeLabel: "Praha",
    countNoun: "pražských čtvrtí",
    inputPlaceholder: "Napiš název pražské čtvrti…",
    listTitle: "Uhádnuté pražské čtvrti",
    emptyHint: "Tipni první pražskou čtvrť a uvidíš ji tady.",
    statCountLabel: "Čtvrti",
    resetConfirm: "Opravdu chcete smazat všechny tipy pražských čtvrtí a začít znovu?",
    finishConfirm: "Opravdu chcete ukončit hru? Najetím na čtvrť na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "population2021",
  },
  brno: {
    mode: "brno",
    geojsonUrl: "/data/brno-districts.geojson",
    indexUrl: "/data/brno-districts-index.json",
    storageKey: "hadej-cesko:guesses:brno:v1",
    modeLabel: "Brno",
    countNoun: "brněnských městských částí",
    inputPlaceholder: "Napiš název brněnské městské části…",
    listTitle: "Uhádnuté brněnské městské části",
    emptyHint: "Tipni první městskou část a uvidíš ji tady.",
    statCountLabel: "Městské části",
    resetConfirm:
      "Opravdu chcete smazat všechny tipy brněnských městských částí a začít znovu?",
    finishConfirm:
      "Opravdu chcete ukončit hru? Najetím na městskou část na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "none",
  },
  metro: {
    mode: "metro",
    geojsonUrl: "/data/metro-stations.geojson",
    indexUrl: "/data/metro-stations-index.json",
    storageKey: "hadej-cesko:guesses:metro:v1",
    modeLabel: "Metro",
    countNoun: "stanic metra",
    inputPlaceholder: "Napiš název stanice metra…",
    listTitle: "Uhádnuté stanice metra",
    emptyHint: "Tipni první stanici a uvidíš ji tady.",
    statCountLabel: "Stanice",
    resetConfirm: "Opravdu chcete smazat všechny tipy stanic metra a začít znovu?",
    finishConfirm: "Opravdu chcete ukončit hru? Najetím na stanici na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "none",
    geometryKind: "point",
  },
  reky: {
    mode: "reky",
    geojsonUrl: "/data/rivers.geojson",
    indexUrl: "/data/rivers-index.json",
    storageKey: "hadej-cesko:guesses:reky:v1",
    modeLabel: "Řeky",
    countNoun: "řek",
    inputPlaceholder: "Napiš název řeky…",
    listTitle: "Uhádnuté řeky",
    emptyHint: "Tipni první řeku a uvidíš ji tady.",
    statCountLabel: "Řeky",
    resetConfirm: "Opravdu chcete smazat všechny tipy řek a začít znovu?",
    finishConfirm: "Opravdu chcete ukončit hru? Najetím na řeku na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "length",
    geometryKind: "line",
    countryBackground: true,
  },
  hory: {
    mode: "hory",
    geojsonUrl: "/data/peaks.geojson",
    indexUrl: "/data/peaks-index.json",
    storageKey: "hadej-cesko:guesses:hory:v1",
    modeLabel: "Hory (m.n.m.)",
    countNoun: "hor",
    inputPlaceholder: "Napiš název hory…",
    listTitle: "Uhádnuté hory",
    emptyHint: "Tipni první horu a uvidíš ji tady.",
    statCountLabel: "Hory",
    resetConfirm: "Opravdu chcete smazat všechny tipy hor a začít znovu?",
    finishConfirm:
      "Opravdu chcete ukončit hru? Najetím na horu na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "elevation",
    geometryKind: "point",
    pointVariant: "peak",
    countryBackground: true,
  },
  "hory-prominence": {
    mode: "hory-prominence",
    geojsonUrl: "/data/peaks-prominence.geojson",
    indexUrl: "/data/peaks-prominence-index.json",
    storageKey: "hadej-cesko:guesses:hory-prominence:v1",
    modeLabel: "Hory (prom.)",
    countNoun: "hor",
    inputPlaceholder: "Napiš název hory…",
    listTitle: "Uhádnuté hory",
    emptyHint: "Tipni první horu a uvidíš ji tady.",
    statCountLabel: "Hory",
    resetConfirm: "Opravdu chcete smazat všechny tipy hor a začít znovu?",
    finishConfirm:
      "Opravdu chcete ukončit hru? Najetím na horu na mapě se vám zobrazí její název.",
    finishedPlaceholder: "Hra ukončena – stiskni Resetovat pro novou.",
    secondaryStat: "prominence",
    geometryKind: "point",
    pointVariant: "peak",
    countryBackground: true,
  },
};
